import { Injectable } from '@angular/core';
import { ILicenciaObtenerItem } from '@modules/sanna/data/models/sanna-licencia-api.interface';
import {
  abstractResolutionConsultationResponse,
  abstractResolutionConsultationResponseItem,
} from '@modules/sanna/interface/abstract.interface';

import { SannaService } from '@modules/sanna/services/sanna.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { IEspacialidades } from '@sanna/data/models/sanna-ciudadano-api.interface';
import { SannaCiudadanoApiService } from '@sanna/services/sanna-ciudadano-api.service';
import { of, combineLatest } from 'rxjs';
import { catchError, map, mergeMap, concatMap } from 'rxjs/operators';

import * as sannaActions from '../actions/sanna.actions';

@Injectable()
export class SannaEffetcs {
  constructor(
    private actions$: Actions,
    private sannaService: SannaService,
    private sannaCiudadanoApiService: SannaCiudadanoApiService,
  ) {}

  lmToRecepcionist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_LICENCES),
      mergeMap(() =>
        this.sannaService.getListLicencesToRecepcionist().pipe(
          map((lsRecordsWithoutProcessing) => sannaActions.LOAD_SANNA_LICENCES_SUCCESS({ lsRecordsWithoutProcessing })),
          catchError((err) => of(sannaActions.LOAD_SANNA_LICENCES_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsToDigitaze$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_LICENCES_TO_DIGITIZE),
      mergeMap((payload) =>
        this.sannaService.getListLicencesToDigitaze(payload.subcomisionesIds).pipe(
          map((lsRecords) => sannaActions.LOAD_SANNA_LICENCES_TO_DIGITIZE_SUCCESS({ lsRecords })),
          catchError((err) => of(sannaActions.LOAD_SANNA_LICENCES_TO_DIGITIZE_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsToType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_LICENCES_TO_TYPE),
      mergeMap((payload) =>
        this.sannaService.getListLicencesToType(payload.subcomisionesIds).pipe(
          map((lsRecords) => sannaActions.LOAD_SANNA_LICENCES_TO_TYPE_SUCCESS({ lsRecords })),
          catchError((err) => of(sannaActions.LOAD_SANNA_LICENCES_TO_TYPE_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsConstants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_LICENCES_CONSTANTS),
      mergeMap(() =>
        this.sannaService.getLicenseConstants().pipe(
          map((constants) => sannaActions.LOAD_SANNA_LICENCES_CONSTANTS_SUCCESS({ constants })),
          catchError((err) => of(sannaActions.LOAD_SANNA_LICENCES_CONSTANTS_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsPartialInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_LICENSE),
      mergeMap((payload) =>
        combineLatest([this.sannaService.getLicense(payload.folio), this.sannaService.getZoneC(payload.folio)]).pipe(
          map((payload) => {
            // combina datos de empleador en la licencia si existen.
            const [license, lsZoneC] = payload;
            if (lsZoneC) license[0].empleador_zona_c = lsZoneC;
            return license[0];
          }),
          catchError((err) => of(sannaActions.LOAD_SANNA_LICENSE_FAIL({ payload: err }))),
        ),
      ),
      concatMap((license: ILicenciaObtenerItem) =>
        combineLatest([
          this.sannaService.getIC1(license.trabajador_rut, license.licencia_folio),
          this.sannaService.getIC2(license.trabajador_rut, license.licencia_folio),
        ]).pipe(
          map((payload) => {
            // combina datos de informe complementario si existen.
            const [ic1, ic2] = payload;
            if (ic1 || ic2) license.informe_complementario = { ic1, ic2 };
            return sannaActions.LOAD_SANNA_LICENSE_SUCCESS({ license: [license] });
          }),
          catchError((err) => of(sannaActions.LOAD_SANNA_LICENSE_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsDiagnostics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_DIAGNOSTIC),
      mergeMap(() =>
        this.sannaService.getLocalDiagnostic().pipe(
          map((diagnostics) => sannaActions.LOAD_SANNA_DIAGNOSTIC_SUCCESS({ diagnostics })),
          catchError((err) => of(sannaActions.LOAD_SANNA_DIAGNOSTIC_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsContingencies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_CONTINGENCIES),
      mergeMap(() =>
        this.sannaService.getContingencies().pipe(
          map((contingencies) => sannaActions.LOAD_SANNA_CONTINGENCIES_SUCCESS({ contingencies })),
          catchError((err) => of(sannaActions.LOAD_SANNA_CONTINGENCIES_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsValidationAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_LICENCES_VALIDATION_ADMIN),
      mergeMap((payload) =>
        this.sannaService.getValidationAdmin(payload.subcomisionesIds).pipe(
          map((lsRecordsValidationAdmin) =>
            sannaActions.LOAD_SANNA_LICENCES_VALIDATION_ADMIN_SUCCESS({ lsRecordsValidationAdmin }),
          ),
          catchError((err) => of(sannaActions.LOAD_SANNA_LICENCES_VALIDATION_ADMIN_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsToValidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_LICENCES_TO_VALIDATE),
      mergeMap((payload) =>
        this.sannaService.getListLicencesValidationRecords(payload.subcomisionesIds).pipe(
          map((lsValidationRecords) => sannaActions.LOAD_SANNA_LICENCES_TO_VALIDATE_SUCCESS({ lsValidationRecords })),
          catchError((err) => of(sannaActions.LOAD_SANNA_LICENCES_TO_VALIDATE_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsDcnReferrer = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_DCN_REFERRER),
      mergeMap((payload) =>
        this.sannaService.getListLicencesToDcnReferrer().pipe(
          map((lsDcnReferrer) => sannaActions.LOAD_SANNA_DCN_REFERRER_SUCCESS({ lsDcnReferrer })),
          catchError((err) => of(sannaActions.LOAD_SANNA_DCN_REFERRER_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  LsPayingEntityConsultation = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_PAYING_ENTITY_CONSULTATION),
      mergeMap((payload) =>
        this.sannaService.getListLicencesToPayingEntityConsultation(payload.entidadesIds).pipe(
          map((lsRecords) => sannaActions.LOAD_SANNA_PAYING_ENTITY_CONSULTATION_SUCCESS({ lsRecords })),
          catchError((err) => of(sannaActions.LOAD_SANNA_PAYING_ENTITY_CONSULTATION_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsPayingEntityProssecing = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_PAYING_ENTITY_PROCESSING),
      mergeMap((payload) =>
        this.sannaService.getListLicencesToPayingEntityProcessing(payload.entidadesIds).pipe(
          map((lsRecords) => sannaActions.LOAD_SANNA_PAYING_ENTITY_PROCESSING_SUCCESS({ lsRecords })),
          catchError((err) => of(sannaActions.LOAD_SANNA_PAYING_ENTITY_PROCESSING_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  especialties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_MEDICAL_SPECIALITY),
      mergeMap(() =>
        this.sannaCiudadanoApiService.obtenerEspecialidades().pipe(
          map((specialties: IEspacialidades) => sannaActions.LOAD_MEDICAL_SPECIALITY_SUCCESS({ specialties })),
          catchError((err) => of(sannaActions.LOAD_MEDICAL_SPECIALITY_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsValidationComptrollerSupport = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_VALIDATION_COMPTROLLER_SUPPORT),
      mergeMap((payload) =>
        this.sannaService.getValidationComptrollerSupport(payload.subcomisionesIds).pipe(
          map((lsValidationComptrollerSupport) =>
            sannaActions.LOAD_SANNA_VALIDATION_COMPTROLLER_SUPPORT_SUCCESS({ lsValidationComptrollerSupport }),
          ),
          catchError((err) => of(sannaActions.LOAD_SANNA_VALIDATION_COMPTROLLER_SUPPORT_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsValidationComptroller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_VALIDATION_COMPTROLLER),
      mergeMap((payload) =>
        this.sannaService.getValidationComptroller(payload.subcomisionesIds).pipe(
          map((lsValidationComptroller) =>
            sannaActions.LOAD_SANNA_VALIDATION_COMPTROLLER_SUCCESS({ lsValidationComptroller }),
          ),
          catchError((err) => of(sannaActions.LOAD_SANNA_VALIDATION_COMPTROLLER_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsValidationPresident$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_VALIDATION_PRESIDENT),
      mergeMap((payload) =>
        this.sannaService.getValidationPresident(payload.subcomisionesIds).pipe(
          map((lsValidationPresident) =>
            sannaActions.LOAD_SANNA_VALIDATION_PRESIDENT_SUCCESS({ lsValidationPresident }),
          ),
          catchError((err) => of(sannaActions.LOAD_SANNA_VALIDATION_PRESIDENT_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsZoneC$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_ZONE_C),
      mergeMap((payload) =>
        this.sannaService.getZoneC(payload.folio).pipe(
          map((lsZoneC) => sannaActions.LOAD_SANNA_ZONE_C_SUCCESS({ lsZoneC })),
          catchError((err) => of(sannaActions.LOAD_SANNA_ZONE_C_FAIL({ payload: err }))),
        ),
      ),
    ),
  );

  lsAbstractResolutionConsultation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sannaActions.LOAD_SANNA_ABSTRACT_RESOL_CONSUL),
      mergeMap((payload) =>
        this.sannaCiudadanoApiService.obtenerConsultaResolucionDetalle(payload.folio).pipe(
          map((LsAbstractResolutionConsultation) =>
            sannaActions.LOAD_SANNA_ABSTRACT_RESOL_CONSUL_SUCCESS({ LsAbstractResolutionConsultation }),
          ),
          catchError((err) => of(sannaActions.LOAD_MEDICAL_SPECIALITY_FAIL({ payload: err }))),
        ),
      ),
    ),
  );
}
